import React from 'react';

import UseModal from 'hooks/use-modal';
import UseToast from 'hooks/use-toast';

import { useMutation, useQuery } from 'react-query';
import { CheckoutService, SubscriptionService } from 'services/payments';
import { billingAddressService } from 'services/auth';

import CartStripe from './CartStripe';
import UserFullName from 'components/common/userfullname/UserFullName';
import CartBillingInformation from './CartBillingInformation';
import Modal from '@core/components/Modal/Modal';
import ActionButton from '@core/components/actionbutton/ActionButton';
import Spinner from '@core/components/spinner/Spinner';

import { getErrorText } from 'utils/geterrortext';
import { isUserLoggedIn } from 'utils/authentication';

import { PROFILE_DETAIL_ERROR, INVALID_DISCOUNT_CODE } from '@core/constants/Errors';
import { MOBILE_SLIDE_UP, MOBILE_FULLSCREEN } from '@core/constants/ModalTypes';

import classes from './CartPayment.module.scss';

const CartPayment = ({ mobileDevice, refetchCart, isSubscription = false, subscriptionPeriod }) => {
	const {
		data: billingInformationData,
		isLoading: loadingBillingInformation,
		refetch: refetchBillingAddress
	} = useQuery(['billing-address', isUserLoggedIn()], billingAddressService.GET, {
		cacheTime: 20 * 60 * 1000
	});

	const { toast, ToastContainer } = UseToast();
	const { show, destroyModal, showModal } = UseModal();
	const {
		show: showStripe,
		destroyModal: destroyStripeModal,
		showModal: showStripeModal
	} = UseModal();
	const {
		show: showFullname,
		destroyModal: destroyFullnameModal,
		showModal: showFullnameModal
	} = UseModal();
	const {
		show: showInvalidCode,
		destroyModal: destroyInvalidCodeModal,
		showModal: showInvalidCodeModal
	} = UseModal();
	const {
		data,
		mutate: checkoutServiceCall,
		isLoading: checkoutServiceCallLoading
	} = useMutation(CheckoutService, {
		onSuccess: () => {
			show && destroyModal();
			showStripeModal();
		},
		onError: (error) => {
			show && destroyModal();
			if (error?.response?.status === +PROFILE_DETAIL_ERROR) {
				showFullnameModal();
			} else if (error?.response?.status === +INVALID_DISCOUNT_CODE) {
				showInvalidCodeModal();
			} else {
				toast(getErrorText(error), 'Alert');
			}
		}
	});

	const {
		data: dataSubscription,
		mutate: checkoutServiceCallSubscription,
		isLoading: checkoutServiceCallLoadingSubscription
	} = useMutation(SubscriptionService, {
		onSuccess: () => {
			show && destroyModal();
			showStripeModal();
		},
		onError: (error) => {
			show && destroyModal();
			if (error?.response?.status === +PROFILE_DETAIL_ERROR) {
				showFullnameModal();
			} else if (error?.response?.status === +INVALID_DISCOUNT_CODE) {
				showInvalidCodeModal();
			} else {
				toast(getErrorText(error), 'Alert');
			}
		}
	});

	const paymentHandler = () => {
		if (!isSubscription)
			checkoutServiceCall();
		else {
			const plan_id = subscriptionPeriod === 'Annual' ? 'yearly' : 'monthly';
			checkoutServiceCallSubscription({"plan_id": plan_id});
		 }
	};
	const invalidCodeHandler = () => {
		refetchCart();
		destroyInvalidCodeModal();
	};
	const desktopBiliingFormSubmit = async () => {
		await refetchBillingAddress();
		// checkoutServiceCall();
	};
	return (
		<>

			{!loadingBillingInformation ? (
				<>
					{!mobileDevice ? (
						<>
							{billingInformationData?.data?.country_iso &&
								billingInformationData?.data?.postal_code &&
								billingInformationData?.data?.city &&
								billingInformationData?.data?.address_line_1 ? (
								<section className='pb-2'>
									<CartBillingInformation
										billingInformationData={billingInformationData}
										isLoadingBillingInfo={loadingBillingInformation}
										refetchBillingAddress={refetchBillingAddress}
										callback={refetchBillingAddress}
									/>
									<ActionButton
										onClick={paymentHandler}
										loading={checkoutServiceCallLoading || checkoutServiceCallLoadingSubscription}
										type="submit"
										className="mt-4">
										Proceed to payment
									</ActionButton>
								</section>
							) : (
								<CartBillingInformation
									billingInformationData={billingInformationData}
									isLoadingBillingInfo={loadingBillingInformation}
									refetchBillingAddress={refetchBillingAddress}
									callback={desktopBiliingFormSubmit}
								/>
							)}
						</>
					) : (
						<>
							<ActionButton
								className="mt-4"
								onClick={showModal}
								loading={checkoutServiceCallLoading}>
								Proceed to payment
							</ActionButton>
							<Modal
								title="Billing address"
								type={MOBILE_FULLSCREEN}
								show={show}
								closeHandler={destroyModal}>
								<CartBillingInformation
									billingInformationData={billingInformationData}
									isLoadingBillingInfo={loadingBillingInformation}
									refetchBillingAddress={refetchBillingAddress}
									callback={refetchBillingAddress}
								/>
								{billingInformationData?.data?.country_iso &&
									billingInformationData?.data?.postal_code &&
									billingInformationData?.data?.city &&
									billingInformationData?.data?.address_line_1 && (
										<ActionButton
											className="mt-5"
											onClick={paymentHandler}
											loading={checkoutServiceCallLoading}>
											Checkout
										</ActionButton>
									)}
							</Modal>
						</>
					)}
				</>
			) : (
				<div className="d-flex align-items-center justify-content-center w-100 py-5">
					<Spinner />
				</div>
			)}
			<Modal type={MOBILE_SLIDE_UP} show={showFullname} closeHandler={destroyFullnameModal}>
				<div className={`${classes['user-fullname-wrapper-modal']} d-flex flex-column gap-2`}>
					<UserFullName
						title=""
						description="One thing before proceeding to your payment. Enter your name below. Please note that your name is not editable later"
						callback={destroyFullnameModal}
					/>
				</div>
			</Modal>
			<Modal show={showInvalidCode} closeHandler={destroyInvalidCodeModal}>
				<div className={`${classes['invalid-code-wrapper']} d-flex flex-column`}>
					<h4>Invalid discount code</h4>
					<p>The code you entered is not valid.</p>
					<ActionButton kind="blank" onClick={invalidCodeHandler}>
						OK
					</ActionButton>
				</div>
			</Modal>
			<Modal show={showStripe} type={MOBILE_SLIDE_UP} closeHandler={destroyStripeModal}>
				<div className={`${classes['stripe-wrapper-modal']} d-flex flex-column gap-2`}>
					<CartStripe
						clientSecret={data ? data?.data?.payment_intent_client_secret : dataSubscription?.data?.payment_intent_client_secret}
						// clientSecret={pics}
						cart_id={data?.data?.cart_id}
						invoice_amount={data ? data?.data?.invoice_amount : dataSubscription?.data?.invoice_amount}
						currency={data ? data?.data?.currency : dataSubscription?.data?.currency}						
					/>
				</div>
			</Modal>
			<ToastContainer />
		</>
	);
};

export default React.memo(CartPayment);
